// Client-side funnel beacon (POST /api/events) — the browser-only moments of the
// buyer journey the backend cannot see: results views, outbound Fiverr clicks
// (every "Open Fiverr inbox" / "View on Fiverr" / "I want to search myself" is a
// plain external link), and the assistant-vs-regular flow choice. One event = one
// `funnel_step` line on the server, riding the BigQuery funnel sink.
//
// Best-effort telemetry over the SAME transport rules as RUM (`sendBatch`):
// never throws into the app, never blocks navigation (`navigator.sendBeacon`
// survives the page unload an outbound click causes — that is exactly the case
// these events exist for). Device class and country are derived SERVER-side
// (User-Agent / edge header); the client never self-reports them.
//
// Events are QUEUED, not posted one per call: `batch.ts` holds them until the page
// goes quiet, a turn ends, the queue is full, or the page hides. See that file for
// the flush policy, and docs/plans/2026-09-06-events-write-rate-design.md for why
// (`/api/events` was 17% of all Cloudflare edge traffic, one request per event).
//
// StrictMode note: view events fire from effects that double-mount in dev —
// `trackEventOnce` de-dupes by key for the session so a remount never
// double-counts a view.

import { enqueueEvent } from "./batch";
import { reportClientError } from "../rum/errors";
import type { ClientFunnelEvent, TrackApp } from "./types";

const COHORT_STORAGE_KEY = "mira.measurement.cohort";
const ENTRY_POINT_STORAGE_KEY = "mira.measurement.entry_point";
const VARIATION_STORAGE_KEY = "mira.measurement.variation";
const ENTRY_PARAMS_STORAGE_KEY = "mira.measurement.entry_params";
const VISIT_STORAGE_KEY = "mira.measurement.visit_id";
const UTM_STORAGE_KEY = "mira.measurement.utm";
// Arms a LANDING PAGE assigned in the browser (`{experiment: arm}`), before any account
// or workspace existed to serve one: the Last Mile hero A/B. Session-persisted like the
// campaign, so the project the landing hands off to carries the arm on its own events.
const LANDING_ARMS_STORAGE_KEY = "mira.measurement.landing_arms";
const MAX_LANDING_ARM_LENGTH = 64;
// Belt matching the server field cap (`ClientFunnelEvent.entry_point`, max 64). The
// value arrives in a craftable URL, so bound it here too rather than letting an absurd
// one 422 the whole batch and take every other event on the page down with it.
const MAX_ENTRY_POINT_LENGTH = 64;
// Belt matching `ClientFunnelEvent.variation` / `CreateTurnRequest.entry_variation`
// (max 32). Same charset the server keeps (`normalize_entry_variation`), so what the
// browser stamps is what the row stores.
const MAX_VARIATION_LENGTH = 32;
const VARIATION_DROP = /[^a-z0-9_-]/g;
// Belt matching `recruiter.entry_points.normalize_entry_params`: the server re-applies
// the same bound to the create-project / turn bodies, this only keeps the stash sane.
const MAX_ENTRY_PARAMS = 20;
const ENTRY_PARAM_KEY = /^[a-z0-9_]{1,40}$/;
const ENTRY_PARAM_VALUE = /^[A-Za-z0-9_.:-]{1,64}$/;
// The server's ceiling on `ClientFunnelEvent.seq` (`le=100_000`).
const MAX_SEQ = 100_000;
// The five standard campaign params, and the server's cap on each
// (`ClientFunnelEvent.utm_*`, max 128). Cut here, in code points, so a long one is
// trimmed rather than 422-ing the whole batch.
const UTM_FIELDS = ["utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content"] as const;
const MAX_UTM_LENGTH = 128;

let trackApp: TrackApp = "web";
let trackProjectId: string | undefined;
let trackCohort: NonNullable<ClientFunnelEvent["cohort"]> = "unassigned";
let trackLiveSearchCohort: NonNullable<ClientFunnelEvent["live_search_cohort"]> | undefined;
let trackHostApp: NonNullable<ClientFunnelEvent["host_app"]> | undefined;
let trackEntryPoint: string | undefined;
let trackVariation: string | undefined;
let trackEntryParams: Record<string, string> | undefined;
let trackUtm: Utm | undefined;
// THE ONE ARMS MAP (`{experiment key: arm}`), stamped on every event beside the
// two per-experiment fields above, which keep flowing until the warehouse
// reads `arms`. Undefined until the workspace names any arm.
let trackArms: Record<string, string> | undefined;
let trackJourneyId: string | undefined;
let trackVisitId: string | undefined;
// This journey's event counter, stamped on every event as `seq`. Its job is to make
// LOSS visible: batching means one refused request costs up to 24 funnel lines
// instead of one, and a line that was never posted is an ABSENT row, not a wrong
// one, so every rate over it reads low with nothing on screen to notice. A gap in
// `(journey_id, seq)` on the sink turns that into a countable fact.
let trackSeq = 0;
// One-shot, because the counter only rises: past the ceiling EVERY later event is
// seq-less, and reporting each one would say nothing new and spend the shared
// client-error budget saying it.
let seqCapReported = false;

function isCohort(value: string | null): value is NonNullable<ClientFunnelEvent["cohort"]> {
  return value === "experiment" || value === "control" || value === "unassigned";
}

function detectedCohort(): NonNullable<ClientFunnelEvent["cohort"]> {
  try {
    const query = new URLSearchParams(globalThis.location?.search ?? "").get("mira_cohort");
    if (isCohort(query)) {
      globalThis.sessionStorage?.setItem(COHORT_STORAGE_KEY, query);
      return query;
    }
    const stored = globalThis.sessionStorage?.getItem(COHORT_STORAGE_KEY) ?? null;
    return isCohort(stored) ? stored : "unassigned";
  } catch {
    return "unassigned";
  }
}

/**
 * The Fiverr surface that referred this buyer, out of the `?mira_source=` landing
 * param the OAuth callback appends, sticky in sessionStorage for the rest of the
 * session.
 *
 * Sticky is the ENTIRE point, and it is why this cannot be a server-side stamp. A
 * CTA-only entry point ("post a project brief", nothing typed) creates NO project at
 * sign-in time: the buyer lands on an empty home, and the project appears minutes
 * later when they finally type. The server never sees a moment where the entry point
 * and the project id are both in hand. Holding the value here and stamping it on
 * every event means the project's own funnel rows carry it whenever it is born,
 * which is what lets BI resolve the entry point per project instead of falling back
 * to 'Direct'.
 *
 * Same mechanism as `detectedCohort` above, deliberately: one landing param, one
 * session-scoped stash, one field on every event.
 */
function detectedEntryPoint(): string | undefined {
  try {
    const query = new URLSearchParams(globalThis.location?.search ?? "").get("mira_source");
    if (query) {
      const bounded = query.slice(0, MAX_ENTRY_POINT_LENGTH);
      globalThis.sessionStorage?.setItem(ENTRY_POINT_STORAGE_KEY, bounded);
      return bounded;
    }
    const stored = globalThis.sessionStorage?.getItem(ENTRY_POINT_STORAGE_KEY);
    return stored ? stored.slice(0, MAX_ENTRY_POINT_LENGTH) : undefined;
  } catch {
    return undefined;
  }
}

/** The referring Fiverr surface bound to this session, for the create-project and turn
 * request bodies (`entry_point`). Undefined when the buyer named no source.
 *
 * The read-side twin of `detectedEntryPoint` above, and the reason it exists: the SPA
 * stamping the surface on every funnel EVENT let BI reconstruct it per project, but
 * only in the warehouse. Sending it on the two creation bodies puts it on the project
 * ROW (`projects.entry_point`, migration 0204), which is the one form a guest journey
 * can rely on - every warehouse tier but the client stamp needs a sign-in. */
export function currentEntryPoint(): string | undefined {
  return trackEntryPoint;
}

function boundedVariation(value: string | null | undefined): string | undefined {
  if (!value) return undefined;
  const cleaned = value.trim().toLowerCase().replace(VARIATION_DROP, "");
  return cleaned ? cleaned.slice(0, MAX_VARIATION_LENGTH) : undefined;
}

/**
 * Which ARM of the referring Fiverr experiment the buyer saw
 * (`/api/v1/entry/start?variation=`), out of the `?mira_variation=` landing param,
 * sticky in sessionStorage exactly like `detectedEntryPoint` above and for the same
 * reason: a CTA-only arrival creates its project minutes later, in the browser, so
 * the value has to survive until then. Stamped on every funnel event AND sent on the
 * create-project / turn bodies (`currentVariation`), which is what makes it a
 * creation stamp on the project row (`projects.entry_variation`).
 *
 * Free text on purpose - the arms are the sender's to name - bounded only by charset
 * and length so a new arm works with no release here.
 */
function detectedVariation(): string | undefined {
  try {
    const query = boundedVariation(
      new URLSearchParams(globalThis.location?.search ?? "").get("mira_variation"),
    );
    if (query) {
      globalThis.sessionStorage?.setItem(VARIATION_STORAGE_KEY, query);
      return query;
    }
    return boundedVariation(globalThis.sessionStorage?.getItem(VARIATION_STORAGE_KEY));
  } catch {
    return undefined;
  }
}

function boundedEntryParams(
  encoded: string | null | undefined,
): Record<string, string> | undefined {
  if (!encoded) return undefined;
  const out: Record<string, string> = {};
  let count = 0;
  for (const [key, value] of new URLSearchParams(encoded)) {
    if (count >= MAX_ENTRY_PARAMS) break;
    if (key in out || !ENTRY_PARAM_KEY.test(key) || !ENTRY_PARAM_VALUE.test(value)) continue;
    out[key] = value;
    count += 1;
  }
  return count > 0 ? out : undefined;
}

/**
 * The entry link's OPEN-ENDED params (`/api/v1/entry/start?mira_entrypoint=...&dark_mode=TRUE`),
 * `{test: cohort}`, out of the `?mira_params=` landing param (one query string holding
 * the whole bag, minted by the server). Sticky in sessionStorage like
 * `detectedVariation` for the same reason: a CTA-only arrival creates its project
 * minutes later, so the bag has to survive until then. Sent on the create-project / turn
 * bodies (`currentEntryParams`), which makes it a creation stamp on the project row
 * (`projects.entry_params`). Not stamped on every funnel event: the server already put
 * it on the arrival's `entry_landing` line.
 */
function detectedEntryParams(): Record<string, string> | undefined {
  try {
    const raw = new URLSearchParams(globalThis.location?.search ?? "").get("mira_params");
    const query = boundedEntryParams(raw);
    if (query) {
      globalThis.sessionStorage?.setItem(
        ENTRY_PARAMS_STORAGE_KEY,
        new URLSearchParams(query).toString(),
      );
      return query;
    }
    return boundedEntryParams(globalThis.sessionStorage?.getItem(ENTRY_PARAMS_STORAGE_KEY));
  } catch {
    return undefined;
  }
}

type UtmField = (typeof UTM_FIELDS)[number];
/** The visit's campaign: whichever of the five `utm_*` params it arrived with. */
export type Utm = Partial<Record<UtmField, string>>;

function boundedUtm(read: (field: UtmField) => unknown): Utm | undefined {
  const out: Utm = {};
  for (const field of UTM_FIELDS) {
    const value = read(field);
    if (typeof value !== "string") continue;
    const trimmed = value.trim();
    if (trimmed) out[field] = Array.from(trimmed).slice(0, MAX_UTM_LENGTH).join("");
  }
  return Object.keys(out).length > 0 ? out : undefined;
}

/**
 * The campaign that brought this visit, out of the standard `utm_*` landing params,
 * sticky in sessionStorage like `detectedEntryPoint` and for the same reason: the
 * project a campaign visit produces is born later (on the Last Mile page, a whole page
 * later), and its funnel rows must still carry the campaign.
 *
 * An arrival that carries ANY utm param replaces the stored campaign whole (the last
 * campaign that brought the tab wins); an arrival with none keeps it. The buyer SPA also
 * passes this campaign through, verbatim, onto every fiverr.com link it builds
 * (`setMiraLinkUtm(currentUtm)`, `lib/fiverr-link.ts`), so a campaign that brought the
 * visit keeps the credit on Fiverr. Only a Google CPC campaign is forwarded. Every
 * campaign is still measured here.
 */
function detectedUtm(): Utm | undefined {
  try {
    const params = new URLSearchParams(globalThis.location?.search ?? "");
    const query = boundedUtm((field) => params.get(field));
    if (query) {
      globalThis.sessionStorage?.setItem(UTM_STORAGE_KEY, JSON.stringify(query));
      return query;
    }
    const stored = globalThis.sessionStorage?.getItem(UTM_STORAGE_KEY);
    if (!stored) return undefined;
    const parsed: unknown = JSON.parse(stored);
    if (parsed === null || typeof parsed !== "object") return undefined;
    const record = parsed as Record<string, unknown>;
    return boundedUtm((field) => record[field]);
  } catch {
    return undefined;
  }
}

/** The campaign bound to this visit (test seam, and for a caller that needs it). */
export function currentUtm(): Utm | undefined {
  return trackUtm ? { ...trackUtm } : undefined;
}

/** The visit's landing campaign for the create-project and turn request bodies
 *  (`entry_utm`), which makes it a creation stamp on the project row
 *  (`projects.entry_utm`, migration 0267). Three fields only: `utm_term` (a search
 *  ad's term is the phrase the person typed) and `utm_content` never leave the
 *  browser for storage. Undefined when the visit carried none of the three. */
export function currentEntryUtm():
  | Pick<Utm, "utm_source" | "utm_medium" | "utm_campaign">
  | undefined {
  if (!trackUtm) return undefined;
  const { utm_source, utm_medium, utm_campaign } = trackUtm;
  if (!utm_source && !utm_medium && !utm_campaign) return undefined;
  return { utm_source, utm_medium, utm_campaign };
}

/** The landing-assigned arms this visit carries (`setLandingArm`), read back from the
 *  session so the app the landing hands off to stamps them too. Bounded per entry. */
function detectedLandingArms(): Record<string, string> | undefined {
  try {
    const stored = globalThis.sessionStorage?.getItem(LANDING_ARMS_STORAGE_KEY);
    if (!stored) return undefined;
    const parsed: unknown = JSON.parse(stored);
    if (parsed === null || typeof parsed !== "object") return undefined;
    const arms: Record<string, string> = {};
    for (const [key, arm] of Object.entries(parsed as Record<string, unknown>)) {
      if (typeof arm !== "string" || !key || !arm) continue;
      arms[key.slice(0, MAX_LANDING_ARM_LENGTH)] = arm.slice(0, MAX_LANDING_ARM_LENGTH);
    }
    return Object.keys(arms).length > 0 ? arms : undefined;
  } catch {
    return undefined;
  }
}

/**
 * Bind an arm a LANDING PAGE assigned in the browser (the Last Mile hero A/B,
 * `lastmile_design`: `a` | `b`), and session-persist it so every later event of the
 * visit carries it, the handed-off project's own included, exactly as the campaign
 * (`utm_*`) travels. Goes into the ONE arms map (`setArms`), so the server's
 * registry check applies: an experiment or arm the registry does not declare is
 * dropped there, metered, never 422'd.
 */
export function setLandingArm(experiment: string, arm: string): void {
  const key = experiment.slice(0, MAX_LANDING_ARM_LENGTH);
  const value = arm.slice(0, MAX_LANDING_ARM_LENGTH);
  if (!key || !value) return;
  setArms({ [key]: value });
  try {
    const stored = { ...(detectedLandingArms() ?? {}), [key]: value };
    globalThis.sessionStorage?.setItem(LANDING_ARMS_STORAGE_KEY, JSON.stringify(stored));
  } catch {
    // Telemetry remains best-effort when storage is unavailable.
  }
}

/**
 * Unbind a landing-assigned arm: the landing rendered WITHOUT an exposure (a forced QA
 * render, a phone) in a tab whose earlier visit had drawn one, and the stale arm must not
 * ride on this visit's events or the project it hands off to. Removes it from the ONE arms
 * map and from the session store, so `initTracking` on the next page has nothing to bring
 * back. A no-op when nothing was bound.
 */
export function clearLandingArm(experiment: string): void {
  const key = experiment.slice(0, MAX_LANDING_ARM_LENGTH);
  if (!key) return;
  if (trackArms && key in trackArms) {
    const rest = { ...trackArms };
    delete rest[key];
    trackArms = Object.keys(rest).length > 0 ? rest : undefined;
  }
  try {
    const stored = detectedLandingArms();
    if (!stored || !(key in stored)) return;
    delete stored[key];
    if (Object.keys(stored).length > 0) {
      globalThis.sessionStorage?.setItem(LANDING_ARMS_STORAGE_KEY, JSON.stringify(stored));
    } else {
      globalThis.sessionStorage?.removeItem(LANDING_ARMS_STORAGE_KEY);
    }
  } catch {
    // Telemetry remains best-effort when storage is unavailable.
  }
}

/** The entry link's open-ended params bound to this session, for the create-project
 * and turn request bodies (`entry_params`). Undefined when the link carried none. */
export function currentEntryParams(): Record<string, string> | undefined {
  return trackEntryParams ? { ...trackEntryParams } : undefined;
}

/** The referring experiment arm bound to this session, for the create-project and
 * turn request bodies (`entry_variation`). Undefined when the buyer arrived with none. */
export function currentVariation(): string | undefined {
  return trackVariation;
}

/**
 * This tab's journey id, minted on first use and stable for the tab's life.
 *
 * Exported so a NON-funnel request can identify itself as the same tab. Today
 * that is the turn-event drain (`GET /api/turns/{id}/events?journey_id=`), which
 * was anonymous in the logs and so could not be joined to the funnel rows of the
 * tab that made it (issue #900). Keep this read-only: the id is minted here and
 * nowhere else, so every surface that reports it reports the same one.
 */
export function currentJourneyId(): string {
  return journeyId();
}

function journeyId(): string {
  if (trackJourneyId !== undefined) return trackJourneyId;
  trackJourneyId = randomId();
  return trackJourneyId;
}

function randomId(): string {
  try {
    return globalThis.crypto.randomUUID();
  } catch {
    const bytes = new Uint32Array(4);
    globalThis.crypto.getRandomValues(bytes);
    return Array.from(bytes, (value) => value.toString(16).padStart(8, "0")).join("");
  }
}

/**
 * This VISIT's id: minted on arrival and held in sessionStorage, so it survives a
 * reload and the OAuth round trip and stays the same for the whole visit.
 *
 * One tier coarser than `journeyId` above, and that is the entire point. A journey
 * id is per page LIFECYCLE, so a reload mints a new one and a conversion counted
 * against it is a conversion in a different bucket from the arrival that produced
 * it. A top-of-funnel rate needs one key on BOTH ends, and none of the existing
 * ids can be it: `project_id` and `journey_id` do not exist yet on a CTA-only or
 * pre-turn arrival, and a user id does not exist for a guest.
 *
 * Sticky across the OAuth hop for free: sessionStorage is scoped to the tab's
 * browsing context, so navigating out to Fiverr's sign-in and back preserves it.
 * Storage may be unavailable (private modes throw), in which case this degrades to
 * an in-memory id — the visit then ends at the next reload, which over-counts
 * landings rather than losing them.
 */
export function currentVisitId(): string {
  if (trackVisitId !== undefined) return trackVisitId;
  try {
    const stored = globalThis.sessionStorage?.getItem(VISIT_STORAGE_KEY);
    if (stored) {
      trackVisitId = stored;
      return trackVisitId;
    }
  } catch {
    // Fall through to minting an in-memory id.
  }
  return beginVisit();
}

/**
 * Mint a NEW visit id, replacing any stored one, and report it.
 *
 * Called when the page is a fresh ARRIVAL rather than a continuation: `currentVisitId`
 * alone cannot tell the two apart once a tab already holds an id, and an entry arrival
 * into an already-open tab session is a new landing (see web's landing-measurement).
 */
export function beginVisit(): string {
  trackVisitId = randomId();
  try {
    globalThis.sessionStorage?.setItem(VISIT_STORAGE_KEY, trackVisitId);
  } catch {
    // Telemetry remains best-effort when storage is unavailable.
  }
  return trackVisitId;
}

/**
 * Whether this tab session ALREADY held a visit id when asked — i.e. the page is a
 * continuation (a reload, a same-tab return) rather than a first arrival.
 *
 * Read-only on purpose: it must not mint, or the first caller would make every later
 * caller read "continuation".
 */
export function hasStoredVisit(): boolean {
  if (trackVisitId !== undefined) return true;
  try {
    return Boolean(globalThis.sessionStorage?.getItem(VISIT_STORAGE_KEY));
  } catch {
    return false;
  }
}

/** Set which SPA is reporting (call once at boot, alongside `initRum`). */
export function initTracking(app: TrackApp): void {
  trackApp = app;
  trackCohort = detectedCohort();
  trackEntryPoint = detectedEntryPoint();
  trackVariation = detectedVariation();
  trackEntryParams = detectedEntryParams();
  trackUtm = detectedUtm();
  const landingArms = detectedLandingArms();
  if (landingArms) setArms(landingArms);
}

/**
 * Bind the referring surface when the PAGE, not a link, is the entry point, and
 * session-persist it the way `detectedEntryPoint` would have. The Last Mile landing
 * page calls this so the project it hands off to carries `entry_point=lastmile` on its
 * row and its funnel lines. It never overrides an explicit `?mira_source=`: a campaign
 * link that names its own source keeps it.
 */
export function setTrackingEntryPoint(entryPoint: string): void {
  if (trackEntryPoint !== undefined) return;
  const bounded = entryPoint.slice(0, MAX_ENTRY_POINT_LENGTH);
  if (!bounded) return;
  trackEntryPoint = bounded;
  try {
    globalThis.sessionStorage?.setItem(ENTRY_POINT_STORAGE_KEY, bounded);
  } catch {
    // Telemetry remains best-effort when storage is unavailable.
  }
}

/** Bind the live-search rail A/B arm (experiment "live_search_rail") once the
 * workspace names it, so every later funnel event slices A vs B without a join.
 * Its OWN axis - `setTrackingCohort` below belongs to the entry-landing
 * measurement and stays untouched by this experiment. */
export function setLiveSearchCohort(
  cohort: NonNullable<ClientFunnelEvent["live_search_cohort"]>,
): void {
  trackLiveSearchCohort = cohort;
}

/** Bind the Fiverr app whose webview this page runs in, once at boot, so every
 * funnel event says it came from the app rather than a browser. The app SPA
 * owns the detection (shared never imports an app); never called in a browser. */
export function setTrackingHostApp(app: NonNullable<ClientFunnelEvent["host_app"]>): void {
  trackHostApp = app;
}

/** Bind the ONE arms map (`{experiment key: arm}`) once the workspace names it,
 * so every later funnel event carries every product experiment's arm under one
 * key. MERGES key by key: a later call naming one experiment leaves the others
 * bound, the same rule `cacheWorkspace` applies to the payload. Pass the
 * DEVICE-EFFECTIVE arms (`effectiveArms`), never the raw served ones: the stamp
 * records what was rendered. The rail's per-experiment setter above keeps being
 * called beside this until the warehouse reads `arms`; the timeframe choice
 * (and any newer experiment) rides ONLY here. */
export function setArms(arms: Record<string, string>): void {
  const merged = { ...(trackArms ?? {}), ...arms };
  trackArms = Object.keys(merged).length > 0 ? merged : undefined;
}

/** Test seam: the arms currently bound. */
export function currentArms(): Record<string, string> | undefined {
  return trackArms ? { ...trackArms } : undefined;
}

/** Bind the referring experiment arm (`mira_variation`) and session-persist it, the
 * way `setTrackingCohort` below does for the cohort. */
export function setTrackingVariation(variation: string): void {
  const bounded = boundedVariation(variation);
  if (!bounded) return;
  trackVariation = bounded;
  try {
    globalThis.sessionStorage?.setItem(VARIATION_STORAGE_KEY, bounded);
  } catch {
    // Telemetry remains best-effort when storage is unavailable.
  }
}

/** Bind a bounded product-experiment assignment supplied by the rollout owner. */
export function setTrackingCohort(cohort: NonNullable<ClientFunnelEvent["cohort"]>): void {
  trackCohort = cohort;
  try {
    globalThis.sessionStorage?.setItem(COHORT_STORAGE_KEY, cohort);
  } catch {
    // Telemetry remains best-effort when storage is unavailable.
  }
}

/**
 * Bind the active project so every subsequent event carries `project_id`
 * without threading it through each component (the workspace is one-project-
 * per-page). An explicit `attrs.project_id` on a call still wins; pass
 * null/undefined to unbind.
 *
 * A project CHANGE also resets the `trackEventOnce` de-dupe set: "start a new
 * project" swaps projects without a page reload, and the new project's view
 * events (results_viewed) must fire again — the de-dupe only exists to absorb
 * StrictMode double-mounts and stage re-entry WITHIN one project.
 */
export function setTrackingProject(projectId: string | null | undefined): void {
  const next = projectId || undefined;
  if (next !== trackProjectId) emittedOnce.clear();
  trackProjectId = next;
}

/**
 * The `seq` to put on the wire, or nothing past the server's ceiling
 * (`ClientFunnelEvent.seq`, `le=100_000`).
 *
 * Omitting it is the point. Validation is all-or-nothing and the counter only
 * ever goes up, so a single out-of-range value would 422 its batch AND every
 * batch after it, silently ending all telemetry from a tab whose only sin was
 * being open a long time. Absent already means "unknown", which is the honest
 * reading of a counter that ran off the end.
 */
export function boundedSeq(seq: number): number | undefined {
  return seq >= 1 && seq <= MAX_SEQ ? seq : undefined;
}

/**
 * This event's `seq`, and a one-shot report the first time there isn't one.
 *
 * Dropping the field is deliberate (see `boundedSeq`), but it is still a DROP of
 * the very thing gap detection rests on: past the ceiling a tab keeps sending
 * events and quietly stops being auditable, and without this nothing anywhere
 * records that such tabs exist (root doctrine 1.12). The `client_error` rail
 * already carries a log-based metric, which is the right shape here.
 */
export function reportedSeq(counter: number): number | undefined {
  const seq = boundedSeq(counter);
  if (seq === undefined && !seqCapReported) {
    seqCapReported = true;
    reportClientError("funnel-seq", "journey counter past its ceiling", { max: MAX_SEQ });
  }
  return seq;
}

/**
 * Attributes riding a funnel event: everything but the step and the two fields
 * the pipeline owns. `seq` is this journey's counter and `age_ms` is measured at
 * flush; a caller passing either would overwrite the very values the gap
 * detection and the emission-time correction rest on, so the type forbids it.
 */
export type TrackAttrs = Omit<ClientFunnelEvent, "step" | "seq" | "age_ms">;

/** Fire one client funnel event. Fire-and-forget; safe on unload paths. */
export function trackEvent(step: ClientFunnelEvent["step"], attrs: TrackAttrs = {}): void {
  trackSeq += 1;
  const event: ClientFunnelEvent = {
    step,
    seq: reportedSeq(trackSeq),
    journey_id: journeyId(),
    // On EVERY event, not just the landing: a conversion rate needs the arrival and
    // the conversion to carry the same key, and this is the only id both ends have.
    visit_id: currentVisitId(),
    cohort: trackCohort,
    live_search_cohort: trackLiveSearchCohort,
    host_app: trackHostApp,
    // The one map beside the rail's field: same value there, one key per experiment.
    arms: trackArms ? { ...trackArms } : undefined,
    entry_point: trackEntryPoint,
    variation: trackVariation,
    // The visit's campaign, on every event like the entry point above.
    ...trackUtm,
    ...attrs,
  };
  if (event.project_id == null && trackProjectId !== undefined) {
    event.project_id = trackProjectId;
  }
  enqueueEvent(trackApp, event);
}

const emittedOnce = new Set<string>();

/**
 * Fire one client funnel event at most once per `key` for the current bound
 * project (the set resets when `setTrackingProject` observes a project
 * change) — for view events emitted from mount effects (StrictMode
 * double-mounts, stage re-entry) where re-emission would double-count.
 */
export function trackEventOnce(
  key: string,
  step: ClientFunnelEvent["step"],
  attrs: TrackAttrs = {},
): void {
  if (emittedOnce.has(key)) return;
  emittedOnce.add(key);
  trackEvent(step, attrs);
}
